import { DownloadOutlined, EditOutlined, PlusOutlined, RightOutlined } from "@ant-design/icons";
import { Link, useNavigate } from "@tanstack/react-router";
import { Avatar, Button, Skeleton, Space, Tag, Tooltip, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Breadcrumbs } from "@/shared/components/Breadcrumbs";
import { DataTable } from "@/shared/components/DataTable";
import { PermissionGate } from "@/shared/components/PermissionGate";
import { usePermission } from "@/shared/hooks/usePermission";
import { CompactDayStrip } from "../components/CompactDayStrip";
import { EmployeeColumnsMenu } from "../components/EmployeeColumnsMenu";
import { EmployeeFilterBar } from "../components/EmployeeFilterBar";
import { EmployeeFormModal } from "../components/EmployeeFormModal";
import { TemporaryPasswordModal } from "../components/TemporaryPasswordModal";
import { activeFilterCount, useEmployeeFilters } from "../hooks/useEmployeeFilters";
import {
	useCreateEmployee,
	useDepartmentOptions,
	usePositionOptions,
	useUpdateEmployee,
} from "../hooks/useEmployees";
import {
	bugun,
	useEmployeeAttendanceCohort,
	useEmployeeRows,
	useEmployeeZoneCohort,
	useZoneOptions,
	WINDOW_DAYS,
} from "../hooks/useEmployeesList";
import {
	type Employee,
	type EmployeeCreatedResult,
	type EmployeeFormValues,
	type EmployeeListItem,
	HR_STATUS_COLORS,
	hrStatusLabel,
} from "../types";
import { type ColumnKey, readColumnPrefs, writeColumnPrefs } from "../utils/columnPrefs";
import { toCreateEmployeeInput } from "../utils/createEmployeeInput";
import { recentDates, summarise, toDayCells } from "../utils/summary";

const { Title } = Typography;

/**
 * Ustundagi havola bosilganda QATOR `onClick` i ishlamasligi uchun.
 *
 * ⚠️ Usiz foydalanuvchi bo'lim nomini bosib xodim profiliga tushardi —
 * havola ko'rsatgan manzil bilan borgan manzil ajralardi.
 *
 * ⚠️⚠️ **Lenta (`CompactDayStrip`) bu himoyani OLMAYDI** — unga bosilganda
 * qator ochilishi KERAK (teskari pin `EmployeesPage.test.tsx` da; u
 * `stopPropagation` qo'shilishi bilan QIZARADI — tekshirildi).
 */
function toxtat(event: React.MouseEvent): void {
	event.stopPropagation();
}

/** Avatar uchun bosh harflar — ikkitadan ko'p emas. */
function boshHarflar(fullName: string): string {
	return fullName
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word) => word[0]?.toUpperCase() ?? "")
		.join("");
}

const CHIZIQCHA = <span className="text-[#8a929e]">—</span>;

export function EmployeesPage() {
	const { t } = useTranslation("employees");
	const navigate = useNavigate();
	const { can } = usePermission();
	const { filters, setFilters } = useEmployeeFilters();
	const [formOpen, setFormOpen] = useState(false);
	const [editing, setEditing] = useState<Employee | null>(null);
	const [createdResult, setCreatedResult] = useState<EmployeeCreatedResult | null>(null);
	const [visibleColumns, setVisibleColumns] = useState<Set<ColumnKey>>(() => readColumnPrefs());

	/**
	 * ⚠️ Tanlov React holatiga VA `localStorage` ga birga yoziladi.
	 * `onChange` to'g'ridan-to'g'ri `setVisibleColumns` ga ulanganda menyu
	 * ishlayotgandek ko'rinardi — boshlang'ich qiymat `readColumnPrefs()`
	 * dan kelardi — lekin sahifa yangilanishi bilan tanlov yo'qolardi.
	 */
	const ustunlarniSaqla = useCallback((next: Set<ColumnKey>) => {
		setVisibleColumns(next);
		writeColumnPrefs(next);
	}, []);

	const query = useEmployeeRows(filters);

	/**
	 * ⚠️ Oyna mount'da BIR MARTA hisoblanadi — har renderda `new Date()`
	 * chaqirilsa yarim tunda so'rov kaliti o'zgarib jadval o'z-o'zidan qayta
	 * yuklanardi (`useReportFilters.ts` naqshi).
	 *
	 * ⚠️ 30 kunlik oyna PROFIL bilan AYNAN bir xil
	 * (`components/EmployeeOverviewTab.tsx`), aks holda ro'yxat va profil
	 * bitta xodim uchun ikki xil foiz ko'rsatardi.
	 */
	const oyna = useMemo(() => recentDates(WINDOW_DAYS, bugun()), []);
	const from = oyna[0];
	const to = oyna[oyna.length - 1];

	// ⚠️ `ids` — SAHIFADAGI qatorlar, ya'ni eng ko'pi 100 ta. Bu kogort
	// marshrutlarining 100 id chegarasi bilan AYNAN mos.
	const ids = useMemo(() => query.items.map((employee) => employee.id), [query.items]);

	const attendanceQuery = useEmployeeAttendanceCohort(ids, from, to);
	const zoneQuery = useEmployeeZoneCohort(ids);

	const { data: departments = [] } = useDepartmentOptions();
	const { data: positions = [] } = usePositionOptions();
	const { data: zoneOptions = [] } = useZoneOptions();

	const departmentNameById = useMemo(
		() => new Map(departments.map((d) => [d.id, d.name])),
		[departments]
	);
	const positionNameById = useMemo(
		() => new Map(positions.map((p) => [p.id, p.name])),
		[positions]
	);

	const createEmployee = useCreateEmployee();
	const updateEmployee = useUpdateEmployee();

	const closeForm = () => {
		setFormOpen(false);
		setEditing(null);
	};

	const handleSubmit = (values: EmployeeFormValues) => {
		if (editing) {
			updateEmployee.mutate(
				{
					id: editing.id,
					input: {
						fullName: values.fullName,
						idCode: values.idCode?.trim() ? values.idCode.trim() : null,
						positionId: values.positionId ?? null,
						departmentId: values.departmentId ?? null,
						managerId: values.managerId ?? null,
						hrStatus: values.hrStatus,
					},
				},
				{ onSuccess: closeForm }
			);
		} else {
			const input = toCreateEmployeeInput(values);
			if (!input) {
				return;
			}
			createEmployee.mutate(input, {
				onSuccess: (result) => {
					closeForm();
					setCreatedResult(result);
				},
			});
		}
	};

	const canAttendance = can("attendance", "read");
	const canZones = can("zones", "read");

	const allColumns: Array<{ key: ColumnKey; column: ColumnsType<EmployeeListItem>[number] }> = [
		{
			key: "fullName",
			column: {
				title: t("list.columns.fullName"),
				key: "fullName",
				width: 250,
				render: (_, employee) => {
					// ⚠️ Faol bo'lmagan xodim qatori susaytiriladi (kanvasdagi
					// «Ta'tilda» qatori): ism `#8a929e`, avatar `opacity: .65`.
					const sust = employee.hrStatus !== "faol";

					return (
						<div className="flex items-center gap-2.5">
							{/*
							 * ⚠️ **Avatarda HAQIQIY RASM YO'Q — faqat bosh harflar.**
							 * `useEmployeePhotoUrl` (`hooks/useEmployees.ts`) har xodim
							 * uchun autentifikatsiyalangan so'rov qiladi,
							 * `URL.createObjectURL` bilan blob yaratadi va uni
							 * `revokeObjectURL` bilan bo'shatadi — 100 qatorli sahifada
							 * bu 100 ta so'rov va 100 ta hayot sikli. Rasm profilda
							 * qoladi.
							 */}
							<Avatar size={26} style={sust ? { opacity: 0.65 } : undefined}>
								<span className="text-[10px]">{boshHarflar(employee.fullName)}</span>
							</Avatar>
							<span>
								<span className={sust ? "text-[#8a929e]" : "font-medium"}>{employee.fullName}</span>
								{/* ⚠️ «Tabel raqami» ALOHIDA ustun EMAS — qiymat shu
								    hujayraning ikkinchi qatorida (kanvasdagi
								    `.who-cell .mono`). */}
								{employee.idCode ? (
									<span className="block font-mono text-[11px] leading-tight text-[#8a929e]">
										{employee.idCode}
									</span>
								) : null}
							</span>
						</div>
					);
				},
			},
		},
		{
			key: "phone",
			column: {
				// Xodimning mobil ilovaga kirish raqami. `users` da yashaydi va
				// ro'yxat javobi uni join bilan olib keladi — usiz «bu xodim qaysi
				// raqam bilan kiradi» savoli panelda javobsiz qolardi. Kanvasda bu
				// ustun yo'q, lekin uni jimgina o'chirish yopilgan savolni qayta
				// ochardi: foydalanuvchi menyudan o'chira oladi.
				title: t("list.columns.phone"),
				dataIndex: "phone",
				key: "phone",
			},
		},
		{
			key: "positionId",
			column: {
				title: t("list.columns.position"),
				key: "positionId",
				render: (_, employee) => {
					const name = employee.positionId ? positionNameById.get(employee.positionId) : undefined;

					return name ? (
						<Link to="/positions" className="text-[#1c5cab] hover:underline" onClick={toxtat}>
							{name}
						</Link>
					) : (
						CHIZIQCHA
					);
				},
			},
		},
		{
			key: "departmentId",
			column: {
				title: t("list.columns.department"),
				key: "departmentId",
				render: (_, employee) => {
					const name = employee.departmentId
						? departmentNameById.get(employee.departmentId)
						: undefined;

					return name ? (
						<Link to="/departments" className="text-[#1c5cab] hover:underline" onClick={toxtat}>
							{name}
						</Link>
					) : (
						CHIZIQCHA
					);
				},
			},
		},
		{
			key: "zone",
			column: {
				title: t("list.columns.zone"),
				key: "zone",
				render: (_, employee) => {
					// ⚠️⚠️ «Yuklanmoqda» va «yo'q» IKKI BOSHQA holat va ular
					// `map.has(id)` bilan AJRATILMAYDI: doiradan tashqaridagi id lar
					// javobdan JIMGINA tashlanadi, ya'ni `has()` ni «yuklandimi» deb
					// o'qish hududsiz xodim qatorida MANGU aylanadigan skeleton
					// qoldirardi. Yuklanish belgisi — SO'ROV holati.
					if (zoneQuery.data === undefined) {
						return <Skeleton.Input active size="small" />;
					}

					const hududlar = zoneQuery.data.get(employee.id) ?? [];

					if (hududlar.length === 0) {
						return CHIZIQCHA;
					}

					const [birinchi, ...qolgani] = hududlar;

					return (
						<span className="inline-flex items-center gap-1.5">
							<Link to="/zones" className="text-[#1c5cab] hover:underline" onClick={toxtat}>
								{birinchi.name}
							</Link>
							{qolgani.length > 0 ? (
								<Tooltip title={qolgani.map((zone) => zone.name).join(", ")}>
									<span className="text-[11px] text-[#8a929e]">+{qolgani.length}</span>
								</Tooltip>
							) : null}
						</span>
					);
				},
			},
		},
		{
			key: "attendance30",
			column: {
				title: t("list.columns.attendance30"),
				key: "attendance30",
				align: "right",
				render: (_, employee) => {
					if (attendanceQuery.data === undefined) {
						return <Skeleton.Input active size="small" />;
					}

					const days = attendanceQuery.data.get(employee.id) ?? [];

					// ⚠️ Bo'sh oynada «0 %» YOZILMAYDI: `summarise([])` `rate: 0`
					// beradi va uni ko'rsatish `utils/summary.ts` ogohlantirgan
					// «yolg'on ayblov» bo'lardi. Lenta ham chizilmaydi.
					if (days.length === 0) {
						return CHIZIQCHA;
					}

					return (
						<div className="flex items-center justify-end gap-2.5">
							{/* ⚠️ Lenta `stopPropagation` OLMAYDI — unga bosilganda
							    qator ochilishi KERAK. */}
							<CompactDayStrip cells={toDayCells(days, oyna)} />
							<b>{summarise(days).rate}%</b>
						</div>
					);
				},
			},
		},
		{
			key: "avgLate",
			column: {
				title: t("list.columns.avgLate"),
				key: "avgLate",
				align: "right",
				render: (_, employee) => {
					if (attendanceQuery.data === undefined) {
						return <Skeleton.Input active size="small" />;
					}

					const korsatkich = summarise(attendanceQuery.data.get(employee.id) ?? []);

					// ⚠️⚠️ Shart — `lateDays === 0`, `avgLateMin === 0` EMAS:
					// `avgLateMin` nol maxrajda `null` qaytaradi, ya'ni `=== 0`
					// jimgina `false` bo'lib hujayrada `null daq` chizilardi.
					return korsatkich.lateDays === 0 ? CHIZIQCHA : <span>{korsatkich.avgLateMin} daq</span>;
				},
			},
		},
		{
			key: "hrStatus",
			column: {
				title: t("list.columns.hrStatus"),
				dataIndex: "hrStatus",
				key: "hrStatus",
				render: (status: Employee["hrStatus"]) => (
					<Tag color={HR_STATUS_COLORS[status]}>{hrStatusLabel(t, status)}</Tag>
				),
			},
		},
		{
			key: "risk",
			column: {
				title: t("list.columns.risk"),
				key: "risk",
				align: "right",
				// ⚠️ **KULRANG SON + bayroqda QIZIL `Tag`, uch bandli rang shkalasi
				// EMAS.** Kanvas 86/74 ni qizil, 61/58 ni sariq ko'rsatib 0–100
				// shkala va ~70/55 chegaralarini nazarda tutadi. HAQIQAT boshqa:
				// `computeRiskScore` (`packages/domain/src/risk.ts`) — shubha
				// hodisalari vaznlarining CHEGARALANMAGAN yig'indisi (low=1,
				// medium=3, high=9), `risk_threshold` standarti esa 10. 70/55 ni
				// ko'chirish o'ylab topilgan chegaralarni mahsulotga kiritardi.
				// Yagona haqiqiy chegara — `riskFlag`.
				render: (_, employee) =>
					employee.riskFlag ? (
						<Tag color="red">{employee.riskScore}</Tag>
					) : (
						<span className="text-[#8a929e]">{employee.riskScore}</span>
					),
			},
		},
	];

	const columns: ColumnsType<EmployeeListItem> = allColumns
		.filter(({ key }) => {
			if (!visibleColumns.has(key)) {
				return false;
			}
			if ((key === "attendance30" || key === "avgLate") && !canAttendance) {
				return false;
			}
			if (key === "zone" && !canZones) {
				return false;
			}

			return true;
		})
		.map(({ column }) => column);

	// ⚠️ «Amallar» ustuni SAQLANADI (kanvasda u yo'q) — tahrirlash tugmasini
	// olib tashlash mavjud testni buzardi va profil tahriri F blokida
	// kafolatlanmagan. Kanvasning hover'da ko'rinadigan `→` chevroni shu bir
	// o'ng chekka hujayraga qo'shiladi.
	columns.push({
		title: t("list.columns.actions"),
		key: "actions",
		align: "right",
		width: 96,
		render: (_, employee) => (
			<Space size={2}>
				<PermissionGate resource="employees" action="update">
					<Button
						type="text"
						icon={<EditOutlined />}
						onClick={(event) => {
							// ⚠️ Usiz tahrirlash tugmasi bosilganda qatorning
							// `onClick` i ham ishlab, foydalanuvchi forma o'rniga
							// profilga tushib ketardi.
							event.stopPropagation();
							setEditing(employee);
							setFormOpen(true);
						}}
						title={t("list.editAction")}
					/>
				</PermissionGate>
				<RightOutlined className="text-[#8a929e] opacity-0 transition-opacity group-hover:opacity-100" />
			</Space>
		),
	});

	const filtrSanogi = activeFilterCount(filters);

	return (
		<div>
			<Breadcrumbs
				items={[
					{ label: t("breadcrumb.organization"), to: "/departments" },
					{ label: t("breadcrumb.employees") },
				]}
			/>

			<div className="mt-3 mb-4 flex flex-wrap items-end justify-between gap-4">
				<div>
					<Title level={3} className="!mb-0">
						{t("list.title")}
					</Title>
					<div className="mt-0.5 text-[12.5px] text-[#565e6b]">
						{t("list.scopeCount", { soni: query.meta.total })}
						{filtrSanogi > 0 ? ` · ${t("list.filterCountSuffix", { soni: filtrSanogi })}` : ""}
					</div>
				</div>

				<Space>
					{/*
					 * ⚠️⚠️ **`<Link to="/employees?departmentId=…">` ISHLAMAYDI.**
					 * `@tanstack/router-core` da `destTo` to'g'ridan-to'g'ri
					 * PATHNAME sifatida ishlatiladi (`resolvePathWithBase`) va `?`
					 * bo'yicha BO'LINMAYDI; natijaviy qidiruv satri alohida
					 * manbadan (`dest.search`) keladi. Shuning uchun manzil
					 * `search` PROPI orqali beriladi — spike'ning 1-varianti. U
					 * typecheck'dan o'tdi, ya'ni 2-(`useNavigate`),
					 * 3-(`modules/reports` ning ichki hodisa nomiga bog'lanish) va
					 * 4-(oddiy `<a href>` bilan to'liq qayta yuklash) variantlari
					 * KERAK BO'LMADI.
					 *
					 * ⚠️ Bu naqsh F blokiga ham tegishli: `EmployeeProfileHeader`
					 * ning bo'lim/lavozim havolasi
					 * `<Link to="/employees" search={{ departmentId }}>` shaklida
					 * yoziladi. `employeesHref`/`applyEmployeeFilters`
					 * (`useEmployeeFilters.ts`) esa navigatsiyasiz filtr qo'yish
					 * uchun qoladi.
					 *
					 * ⚠️ Kalitlar `useReportFilters` bilan AYNAN mos bo'lishi
					 * shart (`dateFrom`, `dateTo`, `departmentId`) — boshqa nom
					 * jimgina e'tiborsiz qolardi.
					 *
					 * ⚠️⚠️ **`zoneId` UZATILMAYDI (Q15).** `useReportFilters` uni
					 * QABUL qiladi, lekin `attendanceMonthly` ning `baseWhere` i
					 * unga qaramaydi (`employeeScopeIds` faqat `employeeId` va
					 * `departmentId` ni o'qiydi) — uzatilsa foydalanuvchi
					 * filtrlanmagan hisobotni «hudud bo'yicha» deb o'qirdi.
					 */}
					<PermissionGate resource="reports" action="read">
						<Link
							to="/reports/$kind"
							params={{ kind: "attendance_monthly" }}
							search={{ dateFrom: from, dateTo: to, departmentId: filters.departmentId }}
							className="inline-flex h-8 items-center gap-2 rounded-md border border-[#e5e6e3] bg-white px-3 text-[12.5px] font-medium text-[#14171c] hover:border-[#d3d5d1]"
						>
							<DownloadOutlined />
							{t("list.exportBtn")}
						</Link>
					</PermissionGate>

					<EmployeeColumnsMenu visible={visibleColumns} onChange={ustunlarniSaqla} />

					<PermissionGate resource="employees" action="create">
						<Button
							type="primary"
							icon={<PlusOutlined />}
							onClick={() => {
								setEditing(null);
								setFormOpen(true);
							}}
						>
							{t("list.newEmployee")}
						</Button>
					</PermissionGate>
				</Space>
			</div>

			{/*
			 * ⚠️⚠️ **`setFilters` YETARLI EMAS — `query.setPage(1)` ham SHART.**
			 * `EmployeeFilterBar` filtrga `page: 1` yozadi, lekin
			 * `usePaginatedQuery` sahifa holatini O'Z ichida (`useState`)
			 * saqlaydi va boshqariladigan rejimni qo'llab-quvvatlamaydi
			 * (`shared/hooks/usePaginatedQuery.ts` — poydevor fayli, bu blok
			 * unga tegmaydi). Usiz 3-sahifada turgan foydalanuvchi filtrni
			 * o'zgartirganda so'rov `["employees", 3, 20, {…}]` kalitiga
			 * tushib, jadval bo'sh chiqardi. Ilgari buni har `Select` o'zi
			 * qilardi (`query.setPage(1)`, to'rt joyda); endi bitta joyda.
			 * Qorovul — `EmployeesPage.test.tsx` dagi «filtr o'zgarganda
			 * jadval 1-sahifaga qaytadi».
			 */}
			<EmployeeFilterBar
				filters={filters}
				onChange={(next) => {
					setFilters(next);
					query.setPage(1);
				}}
				departments={departments}
				positions={positions}
				zones={zoneOptions}
			/>

			{/* ⚠️ Qator butunlay bosiladi va profilga olib boradi; kursor
			    `pointer` bo'lmasa bosiladigan ekani ko'rinmasdi. Ustundagi
			    havolalar («Bo'lim», «Lavozim», «Hudud») va «Amallar» tugmasi
			    `stopPropagation()` bilan himoyalangan — lenta esa ATAYLAB
			    emas. */}
			<DataTable<EmployeeListItem>
				columns={columns}
				query={query}
				onRow={(employee) => ({
					onClick: () => navigate({ to: "/employees/$id", params: { id: employee.id } }),
					style: { cursor: "pointer" },
					className: "group",
				})}
			/>

			<EmployeeFormModal
				open={formOpen}
				editing={editing}
				isSubmitting={createEmployee.isPending || updateEmployee.isPending}
				onCancel={closeForm}
				onSubmit={handleSubmit}
			/>

			<TemporaryPasswordModal result={createdResult} onClose={() => setCreatedResult(null)} />
		</div>
	);
}
